// Corrections in the books (gap #17): reversing a journal entry (journal.reverse) from the Journal,
// with a reason, confirmed on the shared money sheet; the months, open or closed (periods.list), with
// the owner's Reopen of the latest closed one (periods.reopen); and the accountant pack
// (exports.accountantPack), every file an accountant asks for, each saved as a CSV. A refusal says the
// server's words.

import { useState } from 'react';
import { client } from '../api.ts';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { SaveButton } from '../shared/SaveButton.tsx';
import {
  packFileLabel,
  rangeProblem,
  reopenable,
  reopenWarning,
  reversedText,
  rowsText,
  type PackFile,
  type Period,
} from './corrections-model.ts';
import { dayText, monthName, monthOf, todayIn } from './model.ts';
import type { JournalEntry } from './types.ts';
import { Empty, Loading, Money, Panel, errorText, useQuery } from './ui.tsx';
import '../shared/office-lists.css';
import './corrections.css';

export function ReverseSheet({
  entry,
  onClose,
  onReversed,
}: {
  entry: JournalEntry;
  onClose: () => void;
  onReversed: (said: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const amount = entry.lines.reduce((s, l) => s + l.debitCents, 0);
  const reverse = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ number: string; postingDate: string; moved: boolean }>(
        'journal.reverse',
        {
          entryId: entry.id,
          reason: reason.trim(),
          ...(date ? { date } : {}),
        },
      );
      onReversed(reversedText(r, dayText));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title={`Reverse ${entry.number}?`}
      outcome={
        <>
          A new entry posts the mirror image of {entry.number} (<Money cents={amount} />, {entry.memo}): every
          debit becomes a credit. {entry.number} itself stays in the journal, marked reversed.
        </>
      }
      confirm={`Reverse ${entry.number}`}
      ready={!!reason.trim()}
      busy={busy}
      error={error}
      onConfirm={() => void reverse()}
      onClose={onClose}
    >
      <label>
        Why (required)
        <textarea
          className="field"
          rows={2}
          maxLength={300}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <label>
        Date of the reversal (optional)
        <input className="field" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <span className="hint muted small">
          Today if left blank. A date in a closed month posts in the first open one.
        </span>
      </label>
    </MoneySheet>
  );
}

/** Saves one CSV the browser's way: a download, under the name the pack gives it (with its dates). */
function save(file: PackFile) {
  const url = URL.createObjectURL(new Blob([file.content], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function AccountantPack() {
  const today = todayIn();
  const [from, setFrom] = useState(monthOf(today)[0]);
  const [to, setTo] = useState(today);
  const [files, setFiles] = useState<{ files: PackFile[]; from: string; to: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = rangeProblem(from, to);
  const make = async () => {
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ files: PackFile[] }>('exports.accountantPack', { from, to });
      setFiles({ files: r.files, from, to });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Accountant pack">
      <p className="muted">
        What an accountant asks for at month or year end: the trial balance, profit and loss, balance sheet,
        cash flow, the general ledger detail, receivables aging, filed tax returns and bank reconciliations.
        Each is a CSV. Making a pack is recorded in the audit log.
      </p>
      <form
        className="fgrid"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void make();
        }}
      >
        <label>
          <span className="flabel">From</span>
          <input
            className="field"
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setFiles(null);
            }}
          />
        </label>
        <label>
          <span className="flabel">To</span>
          <input
            className="field"
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setFiles(null);
            }}
          />
        </label>
        <div className="row-actions">
          <SaveButton busy={busy} busyLabel="Making the pack…" error={error} onClick={() => void make()}>
            Make the pack
          </SaveButton>
        </div>
      </form>
      {files &&
        (files.files.length ? (
          <ul className="rows pack-files" aria-label="The pack's files">
            {files.files.map((f) => (
              <li key={f.name} className="row between">
                <span>
                  <b>{packFileLabel(f.name)}</b> <span className="muted small">{rowsText(f.content)}</span>
                </span>
                <button type="button" className="btn" onClick={() => save(f)}>
                  Save {packFileLabel(f.name).toLowerCase()}
                </button>
              </li>
            ))}
            <li className="row end">
              <button
                type="button"
                className="btn primary"
                onClick={() => files.files.forEach((f, i) => setTimeout(() => save(f), i * 250))}
              >
                Save all {files.files.length}
              </button>
            </li>
          </ul>
        ) : (
          <Empty>Nothing in these dates.</Empty>
        ))}
    </Panel>
  );
}

/** When a month was closed, on this device's calendar: "Oct 15". */
const closedText = (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { month: 'short', day: 'numeric' }).format(new Date(iso));

/** The months, open or closed; the owner reopens the latest closed one, with a reason (periods.reopen). */
export function Months({ mayReopen, toast }: { mayReopen: boolean; toast: (m: string) => void }) {
  const q = useQuery<{ periods: Period[] }>('periods.list', {});
  const [reopening, setReopening] = useState<Period | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!q.data) return <Loading error={q.error} what="the months" retry={() => void q.reload()} />;
  const latest = reopenable(q.data.periods);
  const reopen = async () => {
    if (!reopening) return;
    setBusy(true);
    setError(null);
    try {
      await client.call('periods.reopen', { date: reopening.startDate, reason: reason.trim() });
      toast(`${monthName(reopening.startDate)} is open again. Close it when you’re done.`);
      setReopening(null);
      setReason('');
      await q.reload();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Months">
      {q.data.periods.length === 0 ? (
        <Empty>Nothing posted yet, so no months to show.</Empty>
      ) : (
        <ul className="rows months">
          {q.data.periods.map((p) => (
            <li key={p.id} className="row between">
              <span>
                <b>{monthName(p.startDate)}</b>{' '}
                <span className={`chip say ${p.status === 'closed' ? 'ok' : ''}`}>
                  {p.status === 'closed' ? 'Closed' : 'Open'}
                </span>
                {p.closedAt && <span className="muted small"> · closed {closedText(p.closedAt)}</span>}
              </span>
              {mayReopen && latest?.id === p.id && (
                <button type="button" className="btn" onClick={() => setReopening(p)}>
                  Reopen {monthName(p.startDate)}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!mayReopen && latest && <p className="muted small">Only the owner reopens a closed month.</p>}
      {reopening && (
        <MoneySheet
          title={`Reopen ${monthName(reopening.startDate)}?`}
          outcome={reopenWarning(monthName(reopening.startDate))}
          confirm={`Reopen ${monthName(reopening.startDate)}`}
          ready={!!reason.trim()}
          busy={busy}
          error={error}
          onConfirm={() => void reopen()}
          onClose={() => {
            setReopening(null);
            setError(null);
          }}
        >
          <label>
            Why (required)
            <textarea
              className="field"
              rows={2}
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
        </MoneySheet>
      )}
    </Panel>
  );
}
